<template>
  <div
    class="flight"
    :class="{ selected: isSelectedFlight, 'extra-padding': isSelectedFlight }"
    @click="!mobile ? selectFlight() : null"
    :id="'uniqueid' + flightDetails.key.replace(/-/g, '')"
  >
    <div class="logo">
      <div class="logo-and-title" v-for="code in airlineCodes" :key="code.index">
        <img class="logo-image" :src="getPathToLogo(code)" alt="airline logo" />
      </div>
    </div>
    <div class="trip-details">
      <template v-for="(leg, index) in flightDetails.odoList">
        <hr v-if="index !== 0" class="leg-divider" :key="`header-${index}`" />
        <div class="trip-leg" :key="`leg-${index}`">
          <span class="trip-leg__details" :key="`details-${index}`">
            {{ getLegDepartureAirportCode(leg) }}
            <b>{{ leg.departureTime }}</b>
            <b-icon icon="airplane" size="is-small"></b-icon>
            <b>{{ leg.arrivalTime }}</b>
            {{ getLegArrivalAirportCode(leg) }}
          </span>
          <span class="trip-leg__properties" :key="`properties-${index}`">
            {{ leg.totalTime }},
            {{ leg.stops === 0 ? "Nonstop" : `${leg.stops} stop(s)` }},
            {{ leg.segments[0].cabinClass.toLowerCase() }}
          </span>
        </div>
      </template>
    </div>
    <div class="price">
      <b>R{{ Math.ceil(flightDetails.amount) }}</b>
      <span v-if="!flightDetails.hasCheckedBagsForAllSegments || flightDetails.bagWarning">
        <div class="bag-warning">Check baggage options!</div>
      </span>
    </div>
    <b-collapse
      class="more-details"
      :class="{ 'no-height': !isSelectedFlight && !mobile}"
      :open="isSelectedFlight"
      position="is-bottom"
      aria-id="contentIdForA11y1"
    >
      <div
        class="details-toggle"
        slot="trigger"
        slot-scope="props"
        aria-controls="contentIdForA11y1"
        @click="detailsToggle()"
      >
        <hr class="details-bar" />
        <b-icon class="toggle-icon" :icon="!props.open ? 'menu-down' : 'menu-up'"></b-icon>
        {{ !props.open ? "Flight details" : "Close details" }}
        <hr class="details-bar" />
      </div>
      <div>
        <b-message
          v-if="differentAirportSameCity"
          type="is-warning"
        >Your flights contain differing airports for the same city, please take note!</b-message>
        <div class="overall">
          <div class="overall-item">
            <b-icon icon="check-circle" size="is-small" type="is-success"></b-icon>Price For All Passengers
          </div>
          <!-- <div class="overall-item longest">
            <b-icon icon="alert-circle" size="is-small" type="is-warning"></b-icon>
            Longest Single Journey {{flightDetails.biggestTravelTime + 1}} hours
          </div>-->
        </div>
        <div class="details-block">
          <div class="leg" v-for="(leg, i) in flightDetails.odoList" :key="`leg-${i}`">
            <b v-if="direction == `outbound`" class="leg-heading">
              {{
              i == 0 ? "Outbound leg" : "Return leg"
              }}
            </b>
            <b v-else-if="direction == `return`" class="leg-heading">Return Leg</b>
            <div v-for="(segment, index) in leg.segments" :key="`segment-${index}`">
              <div v-if="segment.baggage.length > 0" class="baggage">
                <span v-for="(bag, index) in segment.baggage" :key="'bag-' + segment.id + index">
                  <span
                    v-if="bag.type=='CHECKED' && !bag.name.includes('No') && !bag.name.includes('+')"
                  >{{bag.name}} checked baggage</span>
                  <span
                    v-else-if="bag.name.includes('+')"
                  >{{bag.name}} available at passenger details step</span>
                  <span v-else-if="bag.name.includes('No')">No checked baggage</span>
                  <span v-else>{{bag.name}}</span>
                </span>
              </div>
              <div class="segment">
                <div class="route">
                  <b-icon size="is-small" type="is-primary" icon="circle-outline"></b-icon>
                  <div class="route-connect"></div>
                  <b-icon class="tiny-margin-top" size="is-small" type="is-primary" icon="circle"></b-icon>
                </div>
                <div class="locations">
                  <div class="location">
                    <b>{{ segment.origCode }}</b>
                    {{ determineLocationFromCode(segment.origCode).airport }} on
                    {{ niceDate(segment.departureDateTime) }}
                  </div>
                  <div class="location">
                    <b>{{ segment.destCode }}</b>
                    {{ determineLocationFromCode(segment.destCode).airport }} on
                    {{ niceDate(segment.arrivalDateTime) }}
                  </div>
                </div>
              </div>
              <div
                v-if="
                  leg.segments.length > 1 && index < leg.segments.length - 1
                "
                class="layover-warning"
              >
                Layover in
                {{ determineLocationFromCode(segment.destCode).city }}
              </div>
            </div>
          </div>
        </div>
        <div class="select-wrapper">
          <b-button
            outlined
            size="is-small"
            type="is-primary"
            class="book-button mobile-only"
            @click="selectFlightMobile()"
          >{{mobile ? 'Select Flight' : 'Book Flight'}}</b-button>
        </div>
      </div>
    </b-collapse>
  </div>
</template>

<script>
import { mapState } from "vuex";
import moment from "moment";

export default {
  name: "flight",
  props: {
    flightDetails: Object,
    direction: String
  },
  data: function() {
    let scrollOptions = {
      easing: "ease-in",
      offset: -350,
      force: true,
      cancelable: false,
      x: false,
      y: true
    };
    let scrollOptionsMobile = {
      easing: "ease-in",
      offset: -200,
      force: true,
      cancelable: false,
      x: false,
      y: true
    };
    return {
      detailsOpen: false,
      mobile: window.innerWidth <= 781,
      scrollOptions: scrollOptions,
      scrollOptionsMobile: scrollOptionsMobile
    };
  },
  computed: {
    ...mapState([
      "selectedFlights",
      "flags",
      "searchResults",
      "searchParameters"
    ]),
    differentAirportSameCity() {
      if (this.searchParameters.tripType == "return") {
        if (this.flightDetails.odoList.length > 1) {
          let dep = this.flightDetails.odoList[0].segments[0];
          let ret = this.flightDetails.odoList[1].segments[
            this.flightDetails.odoList[1].segments.length - 1
          ];
          let lastSeg = this.flightDetails.odoList[0].segments[
            this.flightDetails.odoList[0].segments.length - 1
          ];
          let firstSeg = this.flightDetails.odoList[1].segments[0];
          if (
            lastSeg.destCode !== firstSeg.origCode ||
            dep.origCode !== ret.destCode
          ) {
            return true;
          }
        }
      }
      return false;
    },
    isSelectedFlight() {
      if (this.direction === "outbound") {
        return this.flightDetails.id === this.selectedFlights.outbound.id
          ? true
          : false;
      } else if (this.direction === "return") {
        return this.flightDetails.id === this.selectedFlights.return.id
          ? true
          : false;
      }
      return false;
    },
    airlineCodes() {
      let usedAirlineCodes = [];
      let airlineCodes = [];
      for (let leg of this.flightDetails.odoList) {
        for (let segment of leg.segments) {
          if (!usedAirlineCodes.includes(segment.airlineCode)) {
            usedAirlineCodes.push(segment.airlineCode);
            airlineCodes.push(segment.airlineCode);
          }
        }
      }
      return airlineCodes;
    },
    departureTimeZone() {
      return this.$store.state.departureTimeZone;
    },
    arrivalTimeZone() {
      return this.$store.state.arrivalTimeZone;
    }
  },
  methods: {
    niceDate(date) {
      return moment(date).format("MMMM Do, HH:mm a");
    },
    determineLocationFromCode(code) {
      if (this.searchResults) {
        return this.searchResults.airportInfos.find(e => {
          return e.iataCode == code;
        });
      }
      return undefined;
    },
    selectFlight() {
      this.detailsOpen = true;
      if (this.direction === "return") {
        this.$store.commit("setReturnFlight", this.flightDetails);
      } else {
        this.$store.commit("setOutboundFlight", this.flightDetails);
      }
      this.$scrollTo(
        `#uniqueid${this.flightDetails.key.replace(/-/g, "")}`,
        500,
        this.scrollOptions
      );
    },
    selectFlightMobile() {
      if (this.direction === "return") {
        this.$store.commit("setReturnFlightMobile", this.flightDetails);
      } else {
        this.$store.commit("setOutboundFlightMobile", this.flightDetails);
      }
      this.$scrollTo(
        `#uniqueid${this.flightDetails.key.replace(/-/g, "")}`,
        500,
        this.scrollOptionsMobile
      );
    },
    getPathToLogo(marketingCode) {
      try {
        return require(`@/assets/images/logos/${marketingCode}.png`);
      } catch (error) {
        return require(`@/assets/images/logos/00.png`);
      }
    },
    getLegDepartureAirportCode(leg) {
      return leg.segments[0].origCode;
    },
    getLegArrivalAirportCode(leg) {
      return leg.segments[leg.segments.length - 1].destCode;
    },
    detailsToggle() {
      this.detailsOpen = !this.detailsOpen;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/settings.scss";

.flight {
  background: $light;
  cursor: pointer;
  padding: 0.75em 1em;
  padding-left: 0.75em;
  box-shadow: 0 1px 3px 0 rgba(32, 33, 36, 0.28);
  border-radius: 0.375rem;
  border-color: $primary-dark;
  border-width: 0.2px;
  display: grid;
  min-height: fit-content;
  grid-template-columns: 50px 1fr 1fr 50px;
  grid-template-rows: auto;
  grid-auto-rows: auto;
  grid-row-gap: 0.75em;
  grid-column-gap: 0.5em;
  grid-template-areas:
    "logo trip-details trip-details price"
    "details details details details";
}

.no-height {
  height: 18px;
}

.bag-warning {
  background: #ffdd57;
  color: rgba(0, 0, 0, 0.7);
  text-align: center;
  white-space: nowrap;
  padding: 0.1rem 0.4rem;
  margin-top: 0.25rem;
  border-radius: 6px;
  font-size: 0.55rem;
  font-weight: 400;
  -webkit-box-shadow: 0px 1px 2px 1px rgba(0, 1, 0, 0.2);
  box-shadow: 0px 1px 2px 1px rgba(0, 1, 0, 0.2);
  z-index: 38;
}

.more-details {
  grid-area: details;
}

.details-toggle {
  font-size: 0.8em;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  font-weight: bold;
  text-transform: uppercase;
}

.details-block {
  padding: 0.75em;
  padding-top: 0;
  display: flex;
  flex-wrap: wrap;
}

.overall {
  margin-left: 0.75em;
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
}

.overall-item {
  white-space: nowrap;
  span {
    margin-left: 0.75em;
    margin-right: 0.5em;
  }
}

.segment {
  display: flex;
}

.leg {
  display: flex;
  flex-direction: column;
}

.leg-heading {
  margin: 0.75em 0.75em 0.75em 0;
}

.layover-warning {
  font-size: 0.8em;
  text-align: center;
  margin: 0.75em;
  color: $primary-dark;
  padding: 0.75em;
  border-radius: 4px;
  background-color: lighten($warning, 30%);
}

.locations {
  display: flex;
  flex-direction: column;
  margin-left: 0.75em;
}

.location {
  font-size: 0.8em;
}

.location:not(:first-child) {
  margin-top: 1.5em;
}

.small-margin-top {
  margin-top: 0.2em;
}

.route {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.baggage {
  padding-left: 0.75em;
  padding-bottom: 0.75em;
  font-size: 0.8em;
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  span + span {
    padding-left: 0.75em;
  }
}

.select-wrapper {
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
}

.route-connect {
  width: 2px;
  height: 100%;
  background-color: $primary-color;
}

.toggle-icon {
  height: 0;
  margin-top: 0.3em;
}

.details-bar {
  background-color: $grey;
  margin: 0 0 0 0.75em;
  width: 100%;
  width: -webkit-fill-available;
}

.leg-details {
  height: 80px;
}

.extra-padding {
  padding: 1.3em 1em !important;
}

.selected {
  box-shadow: 0 0 8px $primary-color;
  border: 2px solid $secondary-color;
}

div {
  font-size: 10px;
}

.book-button {
  margin-left: auto;
}

.logo {
  grid-area: logo;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  * + * {
    margin-top: 8px;
  }
}

.logo-and-title {
  display: flex;
  flex-direction: column;
}

.logo-image {
  max-width: 50px;
  max-height: 40px;
}

.trip-details {
  grid-area: trip-details;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-around;
}

.leg-divider {
  width: 60%;
  margin: 0.3rem auto;
  height: 2px;
  background: $grey;
}

.trip-leg {
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  align-items: center;
}

.trip-leg__details {
  b {
    font-size: 1.2em;
    color: black;
  }
}

.trip-leg__properties {
  font-size: 0.8em;
  color: darken($grey, 40%);
}

.push-right {
  margin-left: auto;
}

.price {
  grid-area: price;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.booking-button {
  margin-top: 0.5em;
}

@media (min-width: 361px) {
  div {
    font-size: 14px;
  }
}

@media (min-width: 781px) {
  .flight {
    min-height: fit-content;
    grid-template-columns: 80px 1fr 1fr 80px;
    grid-template-areas:
      "logo trip-details trip-details price"
      "details details details details";
  }

  .overall {
    margin-top: 0.75em;
    margin-left: 0;
    flex-direction: row;
    justify-content: flex-start;
    flex-wrap: wrap;
  }

  .leg {
    margin-left: 0.75em;
  }

  .details-block {
    flex-wrap: nowrap;
  }

  .flight:hover {
    box-shadow: 0 1px 6px rgba(32, 33, 36, 0.28);
    background: lighten($primary-color, 55%) !important;
    color: $light;
    .details-bar {
      background-color: $light;
    }
  }

  .logo-image {
    max-width: 80px;
    max-height: 40px;
  }
  .trip-details {
    justify-content: space-evenly;
  }
  .leg-divider {
    width: 4px;
    margin: 0;
    height: 4px;
    border-radius: 10px;
    background: black;
  }
}

@media (max-width: 780px) {
  .button {
    font-size: 0.8em !important;
  }
}
</style>

<style lang="scss">
  .collapse {
    display: block;
  }
</style>
